import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import toast from 'react-hot-toast'
import { FAQ, getFAQs, createFAQ, updateFAQ, deleteFAQ, parseKeywords, type FAQCreateInput } from '@/lib/api/faqs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Modal from '@/components/ui/modal'
import TagsInput from '@/components/ui/tags-input'
import { cn } from '@/lib/utils'

const OPERATIONAL_QUESTIONS = new Set([
  '¿Qué métodos de pago aceptan?',
  '¿Hacen envíos? ¿Cuánto cuesta el envío?',
  '¿Puedo devolver un producto? ¿Cuál es la política de cambios?',
  '¿Los productos tienen garantía?',
])

const faqSchema = z.object({
  question: z.string().min(1, 'La pregunta es requerida'),
  answer: z.string().min(1, 'La respuesta es requerida'),
  keywords: z.array(z.string()).default([]),
  active: z.boolean().default(true),
})

type FAQFormValues = z.infer<typeof faqSchema>

interface FAQManagerProps {
  onTestFAQ?: (faq: { question: string; answer: string } | null) => void
}

export default function FAQManager({ onTestFAQ }: FAQManagerProps) {
  const [faqs, setFaqs] = useState<FAQ[]>([])
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null)
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null)

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<FAQFormValues>({
    resolver: zodResolver(faqSchema as any),
    defaultValues: {
      question: '',
      answer: '',
      keywords: [],
      active: true,
    }
  })

  const keywords = watch('keywords')
  const active = watch('active')

  useEffect(() => {
    loadFAQs()
  }, [])

  const loadFAQs = async () => {
    try {
      setLoading(true)
      const data = await getFAQs()
      setFaqs(data)
    } catch (error) {
      console.error('Error loading FAQs:', error)
      toast.error('No se pudieron cargar las FAQs.')
    } finally {
      setLoading(false)
    }
  }

  const openModal = (faq?: FAQ) => {
    if (faq) {
      setEditingFAQ(faq)
      reset({
        question: faq.question,
        answer: faq.answer,
        keywords: parseKeywords(faq.keywords),
        active: faq.active,
      })
    } else {
      setEditingFAQ(null)
      reset({
        question: '',
        answer: '',
        keywords: [],
        active: true,
      })
    }
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingFAQ(null)
    reset()
  }

  const onSubmit = async (data: FAQFormValues) => {
    try {
      const faqData: FAQCreateInput = {
        question: data.question,
        answer: data.answer,
        keywords: keywords,
        active: active,
      }

      if (editingFAQ) {
        await updateFAQ(editingFAQ.id, faqData)
        toast.success('FAQ actualizada')
      } else {
        await createFAQ(faqData)
        toast.success('FAQ creada')
      }
      closeModal()
      loadFAQs()
    } catch (error: any) {
      console.error('Error saving FAQ:', error)
      toast.error(error?.response?.data?.message || 'Error al guardar la FAQ')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar esta FAQ?')) return
    
    try {
      await deleteFAQ(id)
      toast.success('FAQ eliminada')
      loadFAQs()
    } catch (error: any) {
      console.error('Error deleting FAQ:', error)
      toast.error('Error al eliminar la FAQ')
    }
  }

  const handleTest = (faq: FAQ) => {
    onTestFAQ?.({ question: faq.question, answer: faq.answer })
    // Reset después de 5 segundos
    setTimeout(() => {
      onTestFAQ?.(null)
    }, 5000)
  }

  const toggleActive = async (faq: FAQ) => {
    try {
      await updateFAQ(faq.id, { active: !faq.active })
      toast.success(`FAQ ${!faq.active ? 'activada' : 'desactivada'}`)
      loadFAQs()
    } catch (error: any) {
      console.error('Error updating FAQ:', error)
      toast.error('Error al actualizar la FAQ')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Cargando FAQs...</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Preguntas Frecuentes</h3>
          <p className="text-xs sm:text-sm text-gray-500">Gestiona las respuestas automáticas del bot</p>
        </div>
        <Button onClick={() => openModal()} className="bg-[#075E54] hover:bg-[#064E46] text-white w-full sm:w-auto">
          <i className="fas fa-plus mr-2"></i>
          Añadir FAQ
        </Button>
      </div>

      {faqs.length === 0 ? (
        <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-lg border-2 border-dashed">
          <i className="fas fa-question-circle text-3xl sm:text-4xl text-gray-300 mb-3"></i>
          <p className="text-gray-500 mb-4 text-sm sm:text-base">No hay FAQs aún</p>
          <Button onClick={() => openModal()} variant="outline" className="text-[#075E54] border-[#075E54] hover:bg-[#075E54]/5">
            <i className="fas fa-plus mr-2"></i>
            Crear primera FAQ
          </Button>
        </div>
      ) : (
        <div className="space-y-2 sm:space-y-3">
          {faqs.map((faq) => (
            <div
              key={faq.id}
              className={cn(
                "bg-white rounded-lg border shadow-sm overflow-hidden transition-all",
                !faq.active && "opacity-60"
              )}
            >
              <div className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900 text-sm sm:text-base">{faq.question}</h4>
                      {OPERATIONAL_QUESTIONS.has(faq.question) && (
                        <span
                          className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200"
                          title="Creada desde el perfil de tu negocio. Puedes editarla libremente."
                        >
                          Generada automáticamente
                        </span>
                      )}
                      {!faq.active && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          Inactiva
                        </span>
                      )}
                    </div>
                    {expandedFAQ === faq.id ? (
                      <div className="space-y-3">
                        <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap">{faq.answer}</p>
                        {faq.keywords && (
                          <div className="flex flex-wrap gap-1">
                            {parseKeywords(faq.keywords).map((keyword) => (
                              <span key={`${faq.id}-kw-${keyword}`} className="text-xs px-2 py-0.5 bg-[#075E54]/10 text-[#075E54] rounded">
                                {keyword}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{faq.answer}</p>
                    )}
                  </div>
                </div>
                {/* Acciones - Responsive */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-3 sm:mt-4 pt-3 sm:pt-4 border-t gap-2 sm:gap-0">
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                      className="text-xs sm:text-sm"
                    >
                      <i className={`fas ${expandedFAQ === faq.id ? 'fa-eye-slash' : 'fa-eye'} mr-1.5`}></i>
                      {expandedFAQ === faq.id ? 'Ocultar' : 'Ver más'}
                    </Button>
                    {onTestFAQ && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTest(faq)}
                        className="bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 border-[#25D366]/30 text-xs sm:text-sm"
                      >
                        <i className="fas fa-flask mr-1.5"></i>
                        Probar
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 justify-between sm:justify-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-gray-600">Activo</span>
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={faq.active}
                          onChange={() => toggleActive(faq)}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#075E54]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#075E54]"></div>
                      </div>
                    </label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openModal(faq)}
                      className="text-[#075E54] border-[#075E54]/30 hover:bg-[#075E54]/5 hover:border-[#075E54] text-xs sm:text-sm"
                    >
                      <i className="fas fa-edit mr-1.5"></i>
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(faq.id)}
                      className="text-red-600 border-red-200 hover:text-red-700 hover:bg-red-50 hover:border-red-300 text-xs sm:text-sm"
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de creación/edición */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingFAQ ? 'Editar FAQ' : 'Nueva FAQ'}
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Pregunta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <i className="fas fa-question-circle mr-1.5 text-gray-400"></i>
              Pregunta *
            </label>
            <Input {...register('question')} placeholder="¿Cuál es el horario de atención?" className="text-sm" />
            {errors.question && <p className="text-xs text-red-600 mt-1">{errors.question.message}</p>}
          </div>

          {/* Respuesta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <i className="fas fa-comment-dots mr-1.5 text-gray-400"></i>
              Respuesta *
            </label>
            <textarea
              {...register('answer')}
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#075E54] focus:border-transparent resize-none"
              placeholder="Nuestro horario es de lunes a viernes de 9:00 a 18:00..."
            />
            {errors.answer && <p className="text-xs text-red-600 mt-1">{errors.answer.message}</p>}
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <i className="fas fa-hashtag mr-1.5 text-gray-400"></i>
              Keywords/Triggers
            </label>
            <TagsInput
              tags={keywords}
              onChange={(tags) => setValue('keywords', tags)}
              placeholder="Escribe palabras clave y presiona Enter..."
            />
            <p className="text-xs text-gray-500 mt-1.5">
              <i className="fas fa-info-circle mr-1"></i>
              Estas palabras activarán esta respuesta cuando el usuario las mencione
            </p>
          </div>

          {/* Toggle FAQ activa */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  {...register('active')}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#075E54]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#075E54]"></div>
              </label>
              <span className="text-sm font-medium text-gray-700">FAQ activa</span>
            </div>
            <p className="text-xs text-gray-500 sm:ml-auto">
              Las FAQs inactivas no se mostrarán en las respuestas del bot
            </p>
          </div>

          {/* Botones */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={closeModal} className="w-full sm:w-auto">
              <i className="fas fa-times mr-2"></i>
              Cancelar
            </Button>
            <Button type="submit" className="bg-[#075E54] hover:bg-[#064E46] text-white w-full sm:w-auto">
              <i className={`fas ${editingFAQ ? 'fa-save' : 'fa-plus'} mr-2`}></i>
              {editingFAQ ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

